import { CloseButton } from "@components/buttons/close";
import {
  SHButton,
  SHDialog,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import SHSkeleton from "@components/design-systems/sh-skeleton";
import { CheckSVG } from "@components/svgs";
import { useModelScreener } from "@hooks/useModelScreener";
import { ModelScreenerFilterDTO } from "@models/model-screener/entities/modelScreenerFilter";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
} from "@mui/material";
import {
  MSFilterDefaultValueConstants,
  MSFilterIncludeOptionTooltipTemplate,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/constants";
import { MSTableFilterColumnLayout } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-columns";
import { MSFilterDialogContentSkeleton } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/skeletons/filter-modal";
import { MSTableResetButton } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/reset-button";
import {
  setFilters,
  setFilterSettings,
  updateModelScreenerFilters,
} from "@redux/slices/model-screener";
import { RootState, useAppDispatch } from "@redux/store";
import { generateUUID, hexToRGBA } from "@utils";
import { cloneDeep } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useToggle } from "react-use";
import { MSFilterCheckBoxButton } from "../filter-item/checkbox-button";
import { MSFilterDropdownButton } from "../filter-item/dropdown-button";
import { getMSFilterCheckboxButtonPaths } from "../utils";
import { MSFilterSaveModal } from "./save-modal";

type MSTableFilterModalProps = {
  open: boolean;
  data?: ModelScreenerFilterDTO;
  onClose: () => void;
};

export const MSTableFilterModal = ({
  open,
  data,
  onClose,
}: MSTableFilterModalProps) => {
  const { palette } = useTheme();
  const dispatch = useAppDispatch();

  // Refs
  const abortController = useRef<AbortController | null>(null);
  const dialogContentRef = useRef<HTMLDivElement | null>(null);
  const dialogActionsRef = useRef<HTMLDivElement | null>(null);

  // Store
  const isMSFiltering = useSelector(
    (state: RootState) => state.modelScreener.ui.isFiltering,
  );
  const defaultFormData = useSelector((state: RootState) => {
    return state.modelScreener.internal.currentEditingFilterDefaultData;
  });
  const modelScreenerFilterSettings = useSelector((state: RootState) => {
    return state.modelScreener.filterSettings;
  });
  const isLoadingModelScreenerFilterSetting = useSelector(
    (state: RootState) => {
      return state.modelScreener.ui.isLoadingModelScreenerFilterSetting;
    },
  );
  const currentEditingFilterDefaultData = useSelector((state: RootState) => {
    return state.modelScreener.internal.currentEditingFilterDefaultData;
  });

  const { getValues, reset } = useFormContext<ModelScreenerFilterDTO>();

  const {
    loadModelScreenerFilterSettingById,
    deleteModelScreenerFilterSetting,
    setModelScreenerFilterSettingAsDefault,
    updateModelScreenerFilterSetting,
    createModelScreenerFilterSetting,
  } = useModelScreener();

  const checkboxButtonFieldPath = getMSFilterCheckboxButtonPaths();

  // States
  const [dialogKey, setDialogKey] = useState(generateUUID());
  const [isFiltering, toggleIsFiltering] = useToggle(isMSFiltering);
  const [isReloading, toggleIsReloading] = useToggle(false);
  const [saveModalOpening, toggleOpenSaveModal] = useToggle(false);

  // Side effects
  useEffect(() => {
    if (
      (!!isMSFiltering && isFiltering) ||
      !open ||
      (!isMSFiltering && !isFiltering)
    )
      return;
    setTimeout(
      () => toggleIsFiltering(false),
      MSFilterDefaultValueConstants.StandardDelayTimeMs,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMSFiltering]);

  useEffect(() => {
    if (!open) {
      abortController.current?.abort();
      return;
    }
    toggleIsFiltering(false);
    toggleIsReloading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Functions
  const onModalClose = () => {
    onClose();
    setTimeout(
      () => setDialogKey(generateUUID()),
      MSFilterDefaultValueConstants.StandardDelayTimeMs,
    );
  };

  const onResetAll = () => {
    if (!defaultFormData) return;
    reset(defaultFormData);
    dispatch(
      setFilterSettings({
        filterSettings: modelScreenerFilterSettings,
        currentEditingFilter: defaultFormData,
      }),
    );
    setDialogKey(generateUUID());
    setTimeout(
      () => toggleIsReloading(false),
      MSFilterDefaultValueConstants.StandardDelayTimeMs,
    );
  };

  const onApply = (formData: ModelScreenerFilterDTO) => {
    dispatch(updateModelScreenerFilters({ mainFilter: formData }));
  };

  const handleOnSaveButtonClick = () => {
    toggleOpenSaveModal(true);
  };

  const handleOnSelectFilterSetting = async (
    selectedFilter: ModelScreenerFilterDTO,
  ): Promise<boolean> => {
    abortController.current = new AbortController();
    try {
      const response = await loadModelScreenerFilterSettingById({
        selectedFilterSettingId: selectedFilter?.id,
        signal: abortController.current?.signal,
      });
      if (!response) return false;
      setDialogKey(generateUUID());
      onApply(response);
      return true;
    } finally {
      setTimeout(
        () => toggleIsReloading(false),
        MSFilterDefaultValueConstants.StandardDelayTimeMs,
      );
    }
  };

  const handleOnDeleteFilterSetting = async (
    deletedFilter: ModelScreenerFilterDTO,
  ) => {
    await deleteModelScreenerFilterSetting(deletedFilter.id);
  };

  const handleOnSaveFilter = async ({
    filterName,
    isUserDefault,
    overwriteId,
  }: {
    filterName: string;
    isUserDefault: boolean;
    overwriteId?: string;
  }): Promise<boolean> => {
    abortController.current = new AbortController();
    const { shouldIncludeNa, shouldIncludeNoData, columns } = getValues();
    const modelScreenerFilter = {
      id: overwriteId,
      name: filterName,
      isUserDefault: isUserDefault,
      shouldIncludeNa: shouldIncludeNa,
      shouldIncludeNoData: shouldIncludeNoData,
      columns: columns,
    } as ModelScreenerFilterDTO;

    if (overwriteId) {
      const updatedFilterList = await updateModelScreenerFilterSetting(
        modelScreenerFilter,
        abortController.current.signal,
      );
      if (!updatedFilterList) return false;
      dispatch(
        setFilterSettings({
          filterSettings: updatedFilterList,
          currentEditingFilter: modelScreenerFilter,
        }),
      );
    } else {
      const filterList = await createModelScreenerFilterSetting(
        modelScreenerFilter,
        abortController.current.signal,
      );
      if (!filterList) return false;

      const selectedFilterId = filterList.find(
        (x) => x.name === modelScreenerFilter.name,
      )?.id;

      const newFilter = {
        ...modelScreenerFilter,
        id: selectedFilterId ?? "",
      };

      reset(newFilter);
      dispatch(
        setFilterSettings({
          filterSettings: filterList,
          currentEditingFilter: newFilter,
        }),
      );
    }

    return true;
  };

  const handleOnApplyFilterSetting = () => {
    const currentFormData = getValues();
    toggleIsFiltering(true);
    setTimeout(() => {
      onApply(cloneDeep(currentFormData));
      onClose();
    }, 0);
  };

  const handleOnSetFilterAsDefault = async (
    dto: ModelScreenerFilterDTO,
  ): Promise<boolean> => {
    const currentFormData = getValues();
    const currentFilterId = currentFormData.id;

    // Case currently selected
    if (
      currentFilterId === dto?.id &&
      dto?.id !== MSFilterDefaultValueConstants.MSDefaultFilterId
    ) {
      // Save
      const { id, name } = getValues();
      const saveResult = await handleOnSaveFilter({
        filterName: name,
        isUserDefault: true,
        overwriteId: id,
      });

      if (!saveResult) return false;
    }

    // Set as default
    const setDefaultResult = await setModelScreenerFilterSettingAsDefault(
      dto?.id,
    );

    if (!setDefaultResult) return false;

    // Apply
    if (currentFilterId === dto?.id) {
      dispatch(setFilters({ mainFilter: currentFormData }));
    }

    return true;
  };

  return (
    <SHDialog
      keepMounted
      open={open}
      maxWidth={false}
      onClose={onModalClose}
      sx={{
        "& .MuiPaper-root": {
          width: {
            xs: "90%",
            xl: "1550px",
          },
          maxWidth: "90%",
          height: "100%",
          minHeight: "700px",
          padding: "0px 0px",
          position: "relative",
        },
      }}
    >
      {/* Dialog Title with Close Button */}
      <DialogTitle>
        <SHStack gap={2}>
          <SHStack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <SHTypography variant="h2" fontWeight={600}>
              Filter
            </SHTypography>
          </SHStack>

          {/* Toolbar: Filter select, Reset, Apply */}
          <SHStack
            direction={"row"}
            width={"100%"}
            height={"fit-content"}
            alignItems={"flex-start"}
            justifyContent={"space-between"}
          >
            <SHStack
              direction="row"
              flexWrap="wrap"
              // maxWidth={"800px"}
              gap={1}
              height={"fit-content"}
              alignItems={"center"}
            >
              <MSFilterDropdownButton
                buttonTitle={"Load filter"}
                options={modelScreenerFilterSettings}
                isLoadingItem={
                  isFiltering ||
                  isReloading ||
                  isLoadingModelScreenerFilterSetting
                }
                onSelectOption={async (dto) => {
                  toggleIsReloading(true);
                  if (dto.isSystemDefault) {
                    setTimeout(() => {
                      if (currentEditingFilterDefaultData !== null) {
                        onApply(currentEditingFilterDefaultData);
                      }
                      setDialogKey(generateUUID());
                    }, 0);
                    setTimeout(() => {
                      toggleIsReloading(false);
                    }, MSFilterDefaultValueConstants.StandardDelayTimeMs);
                    return true;
                  } else {
                    return await handleOnSelectFilterSetting(dto);
                  }
                }}
                onDeleteOption={handleOnDeleteFilterSetting}
                onMakeDefaultOption={async (dto) => {
                  return await handleOnSetFilterAsDefault(dto);
                }}
              />

              <SHStack direction={"row"} gap={1.5}>
                <SHButton
                  color="primary"
                  size="medium"
                  variant="outlined"
                  startIcon={
                    <CloudDoneOutlinedIcon sx={{ marginTop: "-2px" }} />
                  }
                  disabled={isLoadingModelScreenerFilterSetting}
                  onClick={handleOnSaveButtonClick}
                >
                  Save current filter
                </SHButton>
              </SHStack>

              <SHStack>
                <SHStack display={isReloading ? "block" : "none"}>
                  <SHSkeleton
                    width={152}
                    height={32}
                    delay={false}
                    sx={{ borderRadius: 25 }}
                  />
                </SHStack>

                <SHStack display={!isReloading ? "block" : "none"}>
                  <MSFilterCheckBoxButton
                    key={`include-n-a-${isReloading}-${open}`}
                    disabled={
                      isFiltering || isLoadingModelScreenerFilterSetting
                    }
                    label={
                      <SHTypography fontSize={13} fontWeight={600}>
                        Include N/A
                      </SHTypography>
                    }
                    itemPath={checkboxButtonFieldPath.shouldIncludeNA}
                    tooltipTitle={MSFilterIncludeOptionTooltipTemplate({
                      responseType: "N/A",
                    })}
                    defaultValue={data?.shouldIncludeNa}
                  />
                </SHStack>
              </SHStack>

              <SHStack>
                <SHStack display={isReloading ? "block" : "none"}>
                  <SHSkeleton
                    width={180}
                    height={32}
                    delay={false}
                    sx={{ borderRadius: 25 }}
                  />
                </SHStack>

                <SHStack display={!isReloading ? "block" : "none"}>
                  <MSFilterCheckBoxButton
                    key={`include-no-data-${isReloading}-${open}`}
                    disabled={
                      isFiltering || isLoadingModelScreenerFilterSetting
                    }
                    label={
                      <SHTypography fontSize={13} fontWeight={600}>
                        Include No data
                      </SHTypography>
                    }
                    itemPath={checkboxButtonFieldPath.shouldIncludeNoData}
                    tooltipTitle={MSFilterIncludeOptionTooltipTemplate({
                      responseType: "No data",
                    })}
                    defaultValue={data?.shouldIncludeNoData}
                  />
                </SHStack>
              </SHStack>
            </SHStack>

            <SHStack direction={"row"} alignItems={"center"} gap={1}>
              <MSTableResetButton
                key={`reset-button-${isFiltering}-${isReloading}-${isLoadingModelScreenerFilterSetting}`}
                onClick={() => {
                  toggleIsReloading(true);
                  setTimeout(() => onResetAll(), 0);
                }}
                width={"120px"}
                label={"Reset all"}
                paddingX={1.5}
                height={"32px"}
                disabled={
                  isFiltering ||
                  isReloading ||
                  isLoadingModelScreenerFilterSetting
                }
                fontSize="14px"
              />

              <SHButton
                key={`apply-button-${isFiltering}-${isReloading}-${isLoadingModelScreenerFilterSetting}`}
                variant="contained"
                size="extraMedium"
                disableRipple
                disabled={
                  isFiltering ||
                  isReloading ||
                  isLoadingModelScreenerFilterSetting
                }
                isLoading={isFiltering}
                startIcon={<CheckSVG stroke={"white"} />}
                sx={{
                  width: "fit-content",
                  height: "32px",
                  paddingX: 3,
                  "&:disabled.sh-btn-with-icon": {
                    fill: palette.text.disabled,
                    border: `1px solid ${hexToRGBA(palette.primary.dark, 0.3)}`,
                    backgroundColor: palette.primary[50],
                    color: palette.common.white,
                  },
                }}
                onClick={handleOnApplyFilterSetting}
              >
                Apply
              </SHButton>
            </SHStack>
          </SHStack>
          {/* End Toolbar: Filter select, Reset, Apply */}
        </SHStack>
        <SHStack
          sx={{
            position: "absolute",
            top: "22px",
            right: "25px",
          }}
        >
          <CloseButton width="17px" height="17px" onClick={onModalClose} />
        </SHStack>
      </DialogTitle>
      {/* End Dialog Title with Close Button */}
      <DialogContent
        sx={{
          marginTop: "5px",
          height: "100%",
        }}
      >
        {/* Filters */}
        <SHStack ref={dialogContentRef} width={"100%"} height={"fit-content"}>
          <SHStack display={isReloading ? "block" : "none"} marginTop={2}>
            <MSFilterDialogContentSkeleton />
          </SHStack>
        </SHStack>

        <SHStack
          ref={dialogContentRef}
          width={"100%"}
          height={"fit-content"}
          marginTop={2}
        >
          <SHStack display={!isReloading ? "block" : "none"}>
            <MSTableFilterColumnLayout
              key={`content-${dialogKey}-${data?.id}`}
              columns={data?.columns}
              disabled={isFiltering}
            />
          </SHStack>
        </SHStack>
        {/* End Filters */}
      </DialogContent>
      <DialogActions>
        <SHStack width={"100%"} paddingX={2} paddingY={1}>
          <SHStack
            ref={dialogActionsRef}
            height={"100%"}
            width={"100%"}
            borderRadius={"12px"}
            bgcolor={palette.secondary["50"]}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            paddingX={1.5}
          >
            <MSFilterSaveModal
              open={saveModalOpening}
              onClose={() => {
                toggleOpenSaveModal(false);
              }}
              onSaveFilter={handleOnSaveFilter}
            />
          </SHStack>
        </SHStack>
      </DialogActions>
    </SHDialog>
  );
};
